import dayjs from 'dayjs';
import { Button, Label, Spinner } from 'flowbite-react';
import React, { FormEvent } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { updateAssignmentToJobseeker } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import { GetRecommendationsForRecruiterAPIResponse, SendAssignmentToUserInput, UIApplication } from 'types';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { useUserContext } from 'src/context/UserProvider';
import { Dropdown, getJobSeekerAssignmentExpiry } from 'shared-utils';
import Select, { SingleValue } from 'react-select';
import { useGetAllPrepCoaches } from 'src/hooks/user.hooks';

const RecruiterCandidateUpdateAssignment = ({
	userId,
	onSubmit,
	onFinish,
	userName,
	positionId,
}: {
	userId?: string;
	userName?: string;
	positionId?: string;
	onSubmit?: (data: SendAssignmentToUserInput) => void;
	onFinish?: () => void;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isAccountManagerLoggedIn } = useUserContext();

	const [file, setFile] = React.useState<File | null>();
	const [isLoading, setIsLoading] = React.useState(false);
	const [assginmentFormData, setAssginmentFormData] = React.useState<SendAssignmentToUserInput>({
		positionId: '',
		userId: '',
		prepCoach: '',
	});
	const [, setApplicationData] = React.useState<UIApplication>();

	const { data: prepCoachesData, isLoading: prepCoachesLoading } = useGetAllPrepCoaches();

	const updateAssignmentToJobseekerFn = useMutation(['updateAssignmentToJobseeker'], updateAssignmentToJobseeker, {
		onSuccess(data) {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: `Assignment updated for ${userName}`,
				type: 'success',
			});
			setAssginmentFormData(prev => ({
				...prev,
				positionId: '',
				userId: '',
			}));
			setFile(null);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		let imageUrl = '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			setIsLoading(true);

			setAssginmentFormData(prev => ({
				...prev,
				positionId: '',
				userId: '',
			}));

			const policy = await getPolicyForS3NonVideo(file.name, 'recruiterAssignments', file.type, true);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}

		if (onSubmit) {
			onSubmit({
				...assginmentFormData,
				assignmentFileLink: imageUrl,
				positionId: positionId ?? '',
				userId: userId ?? '',
			});
		} else {
			updateAssignmentToJobseekerFn.mutate({
				newAssignmentDeadline: assginmentFormData.assignmentDeadline ?? new Date(),
				jobSeekerAssignmentDeadline: assginmentFormData.jobSeekerAssignmentDeadline,
				prepCoach: assginmentFormData.prepCoach,
				positionId: positionId ?? '',
				userId: userId ?? '',
			});
		}
	};

	React.useEffect(() => {
		if (userId) {
			const positionRecomendationData: GetRecommendationsForRecruiterAPIResponse | undefined = queryClient.getQueryData(
				['getRecommendationsForPosition', positionId],
			);
			if (positionRecomendationData) {
				const application = positionRecomendationData?.connectedUsers?.find(
					r => r?.user?._id && String(r?.user?._id) === userId,
				)?.application;
				setApplicationData(application);
				setAssginmentFormData({
					positionId: '',
					userId: '',
					prepCoach: String(application?.ongoingRound?.prepCoach?._id || ''),
					assignmentDeadline: application?.ongoingRound?.expiry,
					jobSeekerAssignmentDeadline: application?.ongoingRound?.jobSeekerAssignmentExpiry,
				});
			}
		} else {
			setAssginmentFormData(prev => ({
				...prev,
				positionId: '',
				userId: '',
			}));
		}
	}, []);

	const prepCoachesOptions =
		prepCoachesData?.users?.map(u => ({ label: `${u.name} <${u.email}>`, value: String(u._id) })) ?? [];

	return (
		<form onSubmit={handleSubmit}>
			<div className="mt-[16px]">
				<div className="mb-[8px]">
					<Label>{'Submission deadline '}</Label>
					<span className="text-[gray]">(Optional)</span>
				</div>

				<MobileDateTimePicker
					value={assginmentFormData.assignmentDeadline ? dayjs(assginmentFormData.assignmentDeadline) : null}
					minDateTime={dayjs()}
					format="DD MMM, YYYY, hh:mm A"
					onChange={date => {
						const dateValue = date as { $d?: Date };
						setAssginmentFormData(prev => {
							return {
								...prev,
								assignmentDeadline: dateValue?.$d || undefined,
								jobSeekerAssignmentDeadline: dateValue?.$d ? dayjs(getJobSeekerAssignmentExpiry(dateValue.$d)).toDate() : undefined,
							};
						});
					}}
					sx={{
						'&': {
							width: '100%',
						},
						'& .MuiInputBase-input': {
							padding: '10px',
							borderRadius: '8px',
							backgroundColor: 'white',
							border: '1px solid rgb(209 213 219 / 1)',
							fontSize: '14px',
						},
						'& .MuiInputBase-root': {
							outline: 'none',
							padding: '0px',
							'&:hover': {
								outline: 'none',
							},
						},
						'& fieldset': {
							display: 'none',
						},
					}}
				/>
			</div>

			{/* Prep Coach */}
			{isAccountManagerLoggedIn ? (
				<div className="mt-[16px] whiteBgSelect">
					<div className="mb-[8px]">
						<Label>{'Prep Coach'}</Label>
						<span className="text-[red]">*</span>{' '}
					</div>
					<Select
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						isLoading={prepCoachesLoading}
						options={prepCoachesOptions}
						closeMenuOnSelect
						value={prepCoachesOptions.find(op => op.value === assginmentFormData.prepCoach)}
						onChange={(val: SingleValue<Dropdown>) => {
							if (val) setAssginmentFormData(prev => ({ ...prev, prepCoach: String(val.value) }));
						}}
					/>
				</div>
			) : null}

			{isAccountManagerLoggedIn ? (
				<div className="mt-[16px]">
					<div className="mb-[8px]">
						<Label>{'Jobseeker Submission deadline '}</Label>
						<span className="text-[gray]">(Optional)</span>
					</div>

					<MobileDateTimePicker
						value={assginmentFormData.jobSeekerAssignmentDeadline ? dayjs(assginmentFormData.jobSeekerAssignmentDeadline) : null}
						minDateTime={dayjs()}
						format="DD MMM, YYYY, hh:mm A"
						onChange={date => {
							const dateValue = date as { $d?: Date };
							setAssginmentFormData(prev => {
								return { ...prev, jobSeekerAssignmentDeadline: dateValue?.$d || undefined };
							});
						}}
						sx={{
							'&': {
								width: '100%',
							},
							'& .MuiInputBase-input': {
								padding: '10px',
								borderRadius: '8px',
								backgroundColor: 'white',
								border: '1px solid rgb(209 213 219 / 1)',
								fontSize: '14px',
							},
							'& .MuiInputBase-root': {
								outline: 'none',
								padding: '0px',
								'&:hover': {
									outline: 'none',
								},
							},
							'& fieldset': {
								display: 'none',
							},
						}}
					/>
				</div>
			) : null}

			<Button
				type="submit"
				className="w-full min-h-[42px] mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={isAccountManagerLoggedIn && !assginmentFormData.prepCoach}
			>
				{updateAssignmentToJobseekerFn.isLoading || isLoading ? (
					<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
						<Spinner />
					</div>
				) : (
					'Update Deadline'
				)}
			</Button>
		</form>
	);
};

export default RecruiterCandidateUpdateAssignment;
